import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Switch, Button, Space, Tag, Typography, Input,
  message, Spin, Row, Col, Divider, Tooltip, Modal, Form, InputNumber,
  Select, Popconfirm, Empty, DatePicker, Badge, Breadcrumb,
} from 'antd';
import {
  GlobalOutlined, CopyOutlined, EyeOutlined, EyeInvisibleOutlined,
  ArrowUpOutlined, ArrowDownOutlined, SettingOutlined, SaveOutlined,
  CheckCircleFilled, BgColorsOutlined, LayoutOutlined,
  PlusOutlined, EditOutlined, DeleteOutlined, StarFilled, StarOutlined,
  LinkOutlined, ReadOutlined, TeamOutlined, EnvironmentOutlined,
  FolderOutlined, FileOutlined, DisconnectOutlined, CloudOutlined,
} from '@ant-design/icons';
import {
  FileTextOutlined, MailOutlined, CalendarOutlined,
  FundProjectionScreenOutlined, FormOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { marketingSiteAPI, scenarioSiteAdminAPI, productsAPI, blogAPI, gdriveAPI, scenarioAPI } from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;

const SECTION_TYPE_ICONS = {
  hero: <GlobalOutlined />,
  product_grid: <ShoppingCartOutlined />,
  event_list: <CalendarOutlined />,
  presentation: <FundProjectionScreenOutlined />,
  ai_content: <FileTextOutlined />,
  cta_banner: <GlobalOutlined />,
  inquiry_form: <MailOutlined />,
  testimonial: <FormOutlined />,
  faq: <FileTextOutlined />,
  blog: <FileTextOutlined />,
  survey: <FormOutlined />,
  custom_html: <FileTextOutlined />,
};

const TEMPLATE_DATA = [
  {
    key: 'modern',
    preview: {
      headerBg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      cardRadius: 12,
      shadow: '0 4px 24px rgba(0,0,0,0.12)',
      bodyBg: '#f5f7fa',
      headerStyle: 'gradient',
    },
  },
  {
    key: 'classic',
    preview: {
      headerBg: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)',
      cardRadius: 4,
      shadow: '0 2px 8px rgba(0,0,0,0.08)',
      bodyBg: '#ffffff',
      headerStyle: 'solid',
    },
  },
  {
    key: 'minimal',
    preview: {
      headerBg: '#ffffff',
      cardRadius: 0,
      shadow: 'none',
      bodyBg: '#fafafa',
      headerStyle: 'minimal',
    },
  },
];

const COLOR_PRESET_DATA = [
  { key: 'blue', color: '#1890ff' },
  { key: 'purple', color: '#722ed1' },
  { key: 'cyan', color: '#13c2c2' },
  { key: 'green', color: '#52c41a' },
  { key: 'orange', color: '#fa8c16' },
  { key: 'red', color: '#f5222d' },
  { key: 'geek', color: '#2f54eb' },
  { key: 'dark', color: '#001529' },
];

function _lum(hex) {
  const h = hex.replace('#', '');
  return (0.299 * parseInt(h.substring(0, 2), 16) + 0.587 * parseInt(h.substring(2, 4), 16) + 0.114 * parseInt(h.substring(4, 6), 16)) / 255;
}

function _dk(hex, amt = 0.2) {
  const h = hex.replace('#', '');
  const f = 1 - amt;
  return `rgb(${Math.round(parseInt(h.substring(0, 2), 16) * f)},${Math.round(parseInt(h.substring(2, 4), 16) * f)},${Math.round(parseInt(h.substring(4, 6), 16) * f)})`;
}

function TemplatePreviewCard({ template, brandColor, selected, onClick, t }) {
  const p = template.preview;
  const color = brandColor || '#1890ff';
  const bright = _lum(color) > 0.55;
  const darkColor = bright ? _dk(color, 0.35) : color;

  const getHeaderBg = () => {
    if (template.key === 'minimal') return '#ffffff';
    if (template.key === 'classic') return darkColor;
    return `linear-gradient(135deg, ${darkColor}, ${_dk(darkColor, 0.15)})`;
  };

  const getHeroBg = () => {
    if (template.key === 'minimal') return '#fff';
    if (template.key === 'classic') return `linear-gradient(180deg, ${darkColor}, ${_dk(darkColor, 0.1)})`;
    return `linear-gradient(135deg, ${darkColor}, ${_dk(darkColor, 0.2)})`;
  };

  return (
    <div
      onClick={onClick}
      style={{
        border: selected ? `3px solid ${darkColor}` : '3px solid #e8e8e8',
        borderRadius: 12,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'all 0.3s',
        transform: selected ? 'scale(1.02)' : 'scale(1)',
        boxShadow: selected ? `0 4px 20px rgba(0,0,0,0.15)` : '0 2px 8px rgba(0,0,0,0.06)',
        position: 'relative',
      }}
    >
      {selected && (
        <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 2, background: '#fff', borderRadius: '50%' }}>
          <CheckCircleFilled style={{ fontSize: 22, color: darkColor }} />
        </div>
      )}
      {/* Mini header */}
      <div style={{
        background: getHeaderBg(),
        height: 40,
        display: 'flex',
        alignItems: 'center',
        padding: '0 12px',
        borderBottom: template.key === 'minimal' ? '1px solid #e8e8e8' : 'none',
      }}>
        <div style={{
          width: 8, height: 8, borderRadius: '50%',
          background: template.key === 'minimal' ? color : '#fff',
          marginRight: 8,
        }} />
        <div style={{
          height: 5, width: 40, borderRadius: 3,
          background: template.key === 'minimal' ? '#333' : 'rgba(255,255,255,0.7)',
        }} />
        <div style={{ flex: 1 }} />
        {[1, 2, 3].map(i => (
          <div key={i} style={{
            height: 4, width: 16, borderRadius: 2, marginLeft: 6,
            background: template.key === 'minimal' ? '#bbb' : 'rgba(255,255,255,0.45)',
          }} />
        ))}
      </div>
      {/* Mini hero */}
      <div style={{
        background: getHeroBg(),
        padding: '14px 12px',
        textAlign: 'center',
        borderBottom: template.key === 'minimal' ? `2px solid ${color}` : 'none',
      }}>
        <div style={{
          height: 7, width: 70, borderRadius: template.key === 'classic' ? 0 : 4,
          background: template.key === 'minimal' ? '#333' : 'rgba(255,255,255,0.9)',
          margin: '0 auto 5px',
        }} />
        <div style={{
          height: 4, width: 100, borderRadius: 2,
          background: template.key === 'minimal' ? '#bbb' : 'rgba(255,255,255,0.5)', margin: '0 auto 8px',
        }} />
        <div style={{
          height: 14, width: 50, borderRadius: template.key === 'minimal' ? 0 : template.key === 'classic' ? 3 : 7,
          background: template.key === 'minimal' ? color : '#fff',
          margin: '0 auto',
        }} />
      </div>
      {/* Mini cards */}
      <div style={{
        background: p.bodyBg,
        padding: '8px 10px 10px',
        display: 'flex', gap: 5,
      }}>
        {[1, 2, 3].map(i => (
          <div key={i} style={{
            flex: 1, height: 32,
            background: '#fff',
            borderRadius: p.cardRadius,
            border: template.key === 'minimal' ? '1px solid #e8e8e8' : 'none',
            boxShadow: p.shadow === 'none' ? 'none' : '0 1px 3px rgba(0,0,0,0.06)',
          }} />
        ))}
      </div>
      {/* Label */}
      <div style={{
        padding: '10px 12px',
        borderTop: '1px solid #f0f0f0',
        background: selected ? `${darkColor}08` : '#fff',
      }}>
        <Text strong style={{ fontSize: 13 }}>{t(`dashboard.templates.${template.key}`)}</Text>
        <br />
        <Text type="secondary" style={{ fontSize: 11 }}>{t(`dashboard.templates.${template.key}Desc`)}</Text>
      </div>
    </div>
  );
}

export default function MarketingSiteDashboardPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sections, setSections] = useState([]);
  const [savingSections, setSavingSections] = useState(false);
  const [settingsTitle, setSettingsTitle] = useState('');
  const [settingsDescription, setSettingsDescription] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);

  // Template & color state
  const [siteConfig, setSiteConfig] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState('modern');
  const [selectedColor, setSelectedColor] = useState('#1890ff');
  const [customColor, setCustomColor] = useState('');
  const [savingStyle, setSavingStyle] = useState(false);

  // Products state
  const [siteProducts, setSiteProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [productModalMode, setProductModalMode] = useState('create'); // 'create' | 'edit' | 'link'
  const [editingSp, setEditingSp] = useState(null);
  const [productForm] = Form.useForm();
  const [productSaving, setProductSaving] = useState(false);
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [catalogSearch, setCatalogSearch] = useState('');

  // Events state
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm] = Form.useForm();
  const [eventSaving, setEventSaving] = useState(false);
  const [regModal, setRegModal] = useState(null);
  const [registrations, setRegistrations] = useState([]);

  // Blog state
  const [blogPosts, setBlogPosts] = useState([]);
  const [blogLoading, setBlogLoading] = useState(false);
  const [blogModalOpen, setBlogModalOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [blogForm] = Form.useForm();
  const [blogSaving, setBlogSaving] = useState(false);

  // Google Drive state
  const [gdriveModalOpen, setGdriveModalOpen] = useState(false);
  const [gdriveFolders, setGdriveFolders] = useState([]);
  const [gdriveLoading, setGdriveLoading] = useState(false);
  const [gdrivePath, setGdrivePath] = useState([]);
  const [gdriveFiles, setGdriveFiles] = useState([]);
  const [gdriveFilesLoading, setGdriveFilesLoading] = useState(false);
  const [gdriveSearch, setGdriveSearch] = useState('');
  const [gdriveLinked, setGdriveLinked] = useState({ folder_id: null, folder_name: null });

  const fetchSite = useCallback(async () => {
    setLoading(true);
    try {
      const [siteRes, configRes, secRes] = await Promise.all([
        marketingSiteAPI.get(id),
        marketingSiteAPI.getSiteConfig(id).catch(() => ({ data: null })),
        marketingSiteAPI.getFrontendSections(id).catch(() => ({ data: [] })),
      ]);
      const data = siteRes.data;
      setSite(data);
      setSections(secRes.data || []);
      setSettingsTitle(data.title || '');
      setSettingsDescription(data.description || '');

      const cfg = configRes.data || { site_template: 'modern', brand_color: '#1890ff' };
      setSiteConfig(cfg);
      setSelectedTemplate(cfg.site_template || 'modern');
      setSelectedColor(cfg.brand_color || '#1890ff');
    } catch (e) {
      message.error(t('dashboard.loadError') + ': ' + e.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchSite(); }, [fetchSite]);

  const fetchProducts = useCallback(async () => {
    if (!site?.scenario_id) return;
    setProductsLoading(true);
    try {
      const res = await scenarioSiteAdminAPI.listProducts(site.scenario_id);
      setSiteProducts(res.data || []);
    } catch { /* ignore */ }
    finally { setProductsLoading(false); }
  }, [site?.scenario_id]);

  useEffect(() => { if (site?.scenario_id) fetchProducts(); }, [site?.scenario_id, fetchProducts]);

  const fetchCatalog = async (search = '') => {
    try {
      const res = await productsAPI.getAll({ page: 1, page_size: 50, search });
      setCatalogProducts(res.data?.products || []);
    } catch { /* ignore */ }
  };

  const openCreateProduct = () => {
    setProductModalMode('create');
    setEditingSp(null);
    productForm.resetFields();
    productForm.setFieldsValue({ currency: 'TWD', is_featured: false });
    setProductModalOpen(true);
  };

  const openLinkProduct = () => {
    setProductModalMode('link');
    setEditingSp(null);
    productForm.resetFields();
    setCatalogSearch('');
    fetchCatalog();
    setProductModalOpen(true);
  };

  const openEditProduct = (sp) => {
    setProductModalMode('edit');
    setEditingSp(sp);
    const p = sp.product || {};
    productForm.setFieldsValue({
      name: p.name,
      short_description: p.short_description,
      description: p.description,
      organization: p.organization,
      price: p.price,
      currency: p.currency || 'TWD',
      thumbnail: p.thumbnail,
      is_featured: sp.is_featured,
      custom_price_note: sp.custom_price_note,
    });
    setProductModalOpen(true);
  };

  const ensureProductGridSection = () => ensureSection('product_grid', t('dashboard.sectionTypes.product_grid'), 2);

  const handleSaveProduct = async () => {
    if (!site?.scenario_id) return;
    setProductSaving(true);
    try {
      if (productModalMode === 'link') {
        const values = await productForm.validateFields();
        await scenarioSiteAdminAPI.addProduct(site.scenario_id, {
          product_id: values.product_id,
          is_featured: values.is_featured || false,
          custom_price_note: values.custom_price_note || '',
        });
        message.success(t('dashboard.productsMgmt.added'));
      } else if (productModalMode === 'create') {
        const values = await productForm.validateFields();
        await scenarioSiteAdminAPI.createProductDirect(site.scenario_id, values);
        message.success(t('dashboard.productsMgmt.created'));
      } else {
        const values = await productForm.validateFields();
        await scenarioSiteAdminAPI.updateProductDirect(site.scenario_id, editingSp.id, values);
        message.success(t('dashboard.productsMgmt.updated'));
      }
      setProductModalOpen(false);
      productForm.resetFields();
      if (productModalMode !== 'edit') await ensureProductGridSection();
      fetchProducts();
    } catch (err) {
      if (err.errorFields) {
        message.error(t('dashboard.productsMgmt.fillRequired'));
      } else {
        message.error(`${t('dashboard.productsMgmt.operationFailed')}: ${err.message}`);
      }
    } finally {
      setProductSaving(false);
    }
  };

  const handleRemoveProduct = async (spId) => {
    if (!site?.scenario_id) return;
    try {
      await scenarioSiteAdminAPI.removeProduct(site.scenario_id, spId);
      message.success(t('dashboard.productsMgmt.removed'));
      fetchProducts();
    } catch {
      message.error(t('dashboard.productsMgmt.removeFailed'));
    }
  };

  const handleToggleFeatured = async (sp) => {
    if (!site?.scenario_id) return;
    try {
      await scenarioSiteAdminAPI.updateProduct(site.scenario_id, sp.id, {
        is_featured: !sp.is_featured,
      });
      fetchProducts();
    } catch {
      message.error(t('dashboard.updateFailed'));
    }
  };

  // ── Events ──
  const fetchEvents = useCallback(async () => {
    if (!site?.scenario_id) return;
    setEventsLoading(true);
    try {
      const res = await scenarioSiteAdminAPI.listEvents(site.scenario_id);
      setEvents(res.data || []);
    } catch { /* ignore */ }
    finally { setEventsLoading(false); }
  }, [site?.scenario_id]);

  useEffect(() => { if (site?.scenario_id) fetchEvents(); }, [site?.scenario_id, fetchEvents]);

  const openCreateEvent = () => {
    setEditingEvent(null);
    eventForm.resetFields();
    setEventModalOpen(true);
  };

  const openEditEvent = (evt) => {
    setEditingEvent(evt);
    eventForm.setFieldsValue({
      title: evt.title,
      description: evt.description,
      location: evt.location,
      max_attendees: evt.max_attendees,
      event_date: evt.event_date ? dayjs(evt.event_date) : null,
      registration_deadline: evt.registration_deadline ? dayjs(evt.registration_deadline) : null,
      is_active: evt.is_active,
    });
    setEventModalOpen(true);
  };

  const handleSaveEvent = async () => {
    if (!site?.scenario_id) return;
    setEventSaving(true);
    try {
      const values = await eventForm.validateFields();
      const payload = {
        ...values,
        event_date: values.event_date?.toISOString() || null,
        registration_deadline: values.registration_deadline?.toISOString() || null,
      };
      if (editingEvent) {
        await scenarioSiteAdminAPI.updateEvent(site.scenario_id, editingEvent.id, payload);
        message.success(t('dashboard.eventsMgmt.eventUpdated'));
      } else {
        await scenarioSiteAdminAPI.createEvent(site.scenario_id, payload);
        message.success(t('dashboard.eventsMgmt.eventCreated'));
        await ensureSection('event_list', t('dashboard.sectionTypes.event_list'), 4);
      }
      setEventModalOpen(false);
      eventForm.resetFields();
      fetchEvents();
    } catch (err) {
      if (err.errorFields) message.error(t('dashboard.eventsMgmt.fillRequired'));
      else message.error(`${t('dashboard.eventsMgmt.operationFailed')}: ${err.message}`);
    } finally { setEventSaving(false); }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!site?.scenario_id) return;
    try {
      await scenarioSiteAdminAPI.deleteEvent(site.scenario_id, eventId);
      message.success(t('dashboard.eventsMgmt.eventDeleted'));
      fetchEvents();
    } catch { message.error(t('dashboard.eventsMgmt.deleteFailed')); }
  };

  const handleToggleEventActive = async (evt) => {
    if (!site?.scenario_id) return;
    try {
      await scenarioSiteAdminAPI.updateEvent(site.scenario_id, evt.id, { is_active: !evt.is_active });
      fetchEvents();
    } catch { message.error(t('dashboard.updateFailed')); }
  };

  const openRegistrations = async (evt) => {
    setRegModal(evt);
    try {
      const res = await scenarioSiteAdminAPI.listRegistrations(site.scenario_id, evt.id);
      setRegistrations(res.data || []);
    } catch { setRegistrations([]); }
  };

  // ── Blog ──
  const fetchBlog = useCallback(async () => {
    setBlogLoading(true);
    try {
      const res = await blogAPI.getPosts({ page: 1, page_size: 100 });
      setBlogPosts(res.data?.posts || []);
    } catch { /* ignore */ }
    finally { setBlogLoading(false); }
  }, []);

  useEffect(() => { fetchBlog(); }, [fetchBlog]);

  const openCreatePost = () => {
    setEditingPost(null);
    blogForm.resetFields();
    blogForm.setFieldsValue({ is_published: false });
    setBlogModalOpen(true);
  };

  const openEditPost = (post) => {
    setEditingPost(post);
    blogForm.setFieldsValue({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      content: post.content,
      featured_image: post.featured_image,
      tags: post.tags,
      is_published: post.is_published,
    });
    setBlogModalOpen(true);
  };

  const handleSavePost = async () => {
    setBlogSaving(true);
    try {
      const values = await blogForm.validateFields();
      if (!values.slug) {
        values.slug = values.title.toLowerCase().replace(/[^a-z0-9\u4e00-\u9fff]+/g, '-').replace(/(^-|-$)/g, '');
      }
      if (editingPost) {
        await blogAPI.updatePost(editingPost.id, values);
        message.success(t('dashboard.blogMgmt.postUpdated'));
      } else {
        await blogAPI.createPost(values);
        message.success(t('dashboard.blogMgmt.postCreated'));
      }
      setBlogModalOpen(false);
      blogForm.resetFields();
      fetchBlog();
    } catch (err) {
      if (err.errorFields) message.error(t('dashboard.blogMgmt.fillRequired'));
      else message.error(`${t('dashboard.blogMgmt.operationFailed')}: ${err.message}`);
    } finally { setBlogSaving(false); }
  };

  const handleDeletePost = async (postId) => {
    try {
      await blogAPI.deletePost(postId);
      message.success(t('dashboard.blogMgmt.postDeleted'));
      fetchBlog();
    } catch { message.error(t('dashboard.blogMgmt.deleteFailed')); }
  };

  const handleTogglePostPublish = async (post) => {
    try {
      await blogAPI.updatePost(post.id, { is_published: !post.is_published });
      fetchBlog();
    } catch { message.error(t('dashboard.updateFailed')); }
  };

  // ── Google Drive ──
  const loadGdriveLinkedFiles = useCallback(async (folderId) => {
    if (!folderId) return;
    setGdriveFilesLoading(true);
    try {
      const res = await gdriveAPI.browse(folderId);
      setGdriveFiles(res.data || []);
    } catch { /* ignore */ }
    finally { setGdriveFilesLoading(false); }
  }, []);

  useEffect(() => {
    if (!site?.scenario_id) return;
    scenarioAPI.get(site.scenario_id).then(res => {
      const s = res.data;
      if (s?.gdrive_folder_id) {
        setGdriveLinked({ folder_id: s.gdrive_folder_id, folder_name: s.gdrive_folder_name });
        loadGdriveLinkedFiles(s.gdrive_folder_id);
      }
    }).catch(() => {});
  }, [site?.scenario_id, loadGdriveLinkedFiles]);

  const openGdrivePicker = async () => {
    setGdriveModalOpen(true);
    setGdrivePath([]);
    setGdriveSearch('');
    setGdriveLoading(true);
    try {
      const res = await gdriveAPI.listFolders(null);
      setGdriveFolders(res.data || []);
    } catch (e) {
      message.error(t('dashboard.gdrive.loadFailed') + ': ' + e.message);
    } finally { setGdriveLoading(false); }
  };

  const loadGdriveFolders = async (parentId) => {
    setGdriveLoading(true);
    try {
      const res = await gdriveAPI.listFolders(parentId);
      setGdriveFolders(res.data || []);
    } catch (e) {
      message.error(t('common.loadFailed') + ': ' + e.message);
    } finally { setGdriveLoading(false); }
  };

  const navigateGdriveFolder = async (folderId, folderName) => {
    setGdrivePath(prev => [...prev, { id: folderId, name: folderName }]);
    await loadGdriveFolders(folderId);
  };

  const navigateGdriveBreadcrumb = async (index) => {
    if (index < 0) {
      setGdrivePath([]);
      await loadGdriveFolders(null);
    } else {
      const newPath = gdrivePath.slice(0, index + 1);
      setGdrivePath(newPath);
      await loadGdriveFolders(newPath[newPath.length - 1].id);
    }
  };

  const selectGdriveFolder = async (folderId, folderName) => {
    if (!site?.scenario_id) return;
    try {
      await scenarioAPI.update(site.scenario_id, {
        gdrive_folder_id: folderId,
        gdrive_folder_name: folderName,
      });
      setGdriveLinked({ folder_id: folderId, folder_name: folderName });
      message.success(`${t('dashboard.gdrive.linked')}: ${folderName}`);
      setGdriveModalOpen(false);
      loadGdriveLinkedFiles(folderId);
    } catch (e) {
      message.error(t('dashboard.gdrive.setFailed') + ': ' + e.message);
    }
  };

  const unlinkGdrive = async () => {
    if (!site?.scenario_id) return;
    try {
      await scenarioAPI.update(site.scenario_id, {
        gdrive_folder_id: '',
        gdrive_folder_name: '',
      });
      setGdriveLinked({ folder_id: null, folder_name: null });
      setGdriveFiles([]);
      message.success(t('dashboard.gdrive.unlinked'));
    } catch (e) {
      message.error(t('dashboard.gdrive.unlinkFailed') + ': ' + e.message);
    }
  };

  // ── Helper: ensure site_section exists ──
  const ensureSection = async (sectionType, title, order) => {
    const exists = sections.some(s => s.section_type === sectionType);
    if (exists) return;
    try {
      await scenarioSiteAdminAPI.createSection(site.scenario_id, {
        section_type: sectionType,
        title,
        section_order: order,
        is_visible: true,
      });
      fetchSite();
    } catch { /* may already exist */ }
  };

  const getPublicUrl = () => {
    if (!site) return '';
    const slug = siteConfig?.slug || site.slug;
    return `${window.location.origin}/s/${slug}`;
  };

  const copyPublicUrl = () => {
    navigator.clipboard.writeText(getPublicUrl());
    message.success(t('dashboard.copiedUrl'));
  };

  const handleTogglePublish = async () => {
    try {
      await marketingSiteAPI.update(id, { is_published: !site.is_published });
      message.success(site.is_published ? t('dashboard.unpublished') : t('dashboard.publishedMsg'));
      fetchSite();
    } catch (e) {
      message.error(t('dashboard.updateFailed') + ': ' + e.message);
    }
  };

  const handleSectionVisibility = (index, checked) => {
    const updated = [...sections];
    updated[index] = { ...updated[index], is_visible: checked };
    setSections(updated);
  };

  const handleMoveSection = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= sections.length) return;
    const updated = [...sections];
    const temp = updated[index];
    updated[index] = updated[targetIndex];
    updated[targetIndex] = temp;
    setSections(updated);
  };

  const handleSaveSections = async () => {
    setSavingSections(true);
    try {
      const payload = {
        sections: sections.map((s, i) => ({
          id: s.id,
          section_order: i,
          is_visible: s.is_visible !== false,
        })),
      };
      await marketingSiteAPI.updateFrontendSections(id, payload);
      message.success(t('dashboard.sectionsMgmt.saved'));
      fetchSite();
    } catch (e) {
      message.error(t('dashboard.saveFailed') + ': ' + e.message);
    } finally {
      setSavingSections(false);
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await marketingSiteAPI.update(id, {
        title: settingsTitle,
        description: settingsDescription,
      });
      message.success(t('dashboard.settingsMgmt.saved'));
      fetchSite();
    } catch (e) {
      message.error(t('dashboard.saveFailed') + ': ' + e.message);
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSaveStyle = async () => {
    setSavingStyle(true);
    try {
      await marketingSiteAPI.updateSiteConfig(id, {
        site_template: selectedTemplate,
        brand_color: selectedColor,
      });
      message.success(t('dashboard.style.saved'));
      fetchSite();
    } catch (e) {
      message.error(t('dashboard.saveFailed') + ': ' + e.message);
    } finally {
      setSavingStyle(false);
    }
  };

  const handleColorSelect = (color) => {
    setSelectedColor(color);
    setCustomColor('');
  };

  const handleCustomColorChange = (e) => {
    const val = e.target.value;
    setCustomColor(val);
    if (/^#[0-9a-fA-F]{6}$/.test(val)) {
      setSelectedColor(val);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!site) {
    return (
      <Card>
        <Text type="secondary">{t('dashboard.notFound')}</Text>
      </Card>
    );
  }

  const sectionColumns = [
    {
      title: t('dashboard.sectionsMgmt.type'), width: 160,
      render: (_, record) => (
        <Space>
          {SECTION_TYPE_ICONS[record.section_type] || <GlobalOutlined />}
          <Tag>{t(`dashboard.sectionTypes.${record.section_type}`) || record.section_type}</Tag>
        </Space>
      ),
    },
    {
      title: t('dashboard.sectionsMgmt.sectionTitle'), dataIndex: 'title', ellipsis: true,
      render: (v) => v || <Text type="secondary">-</Text>,
    },
    {
      title: t('dashboard.sectionsMgmt.visible'), width: 80,
      render: (_, record, index) => (
        <Switch
          size="small"
          checked={record.is_visible !== false}
          onChange={(checked) => handleSectionVisibility(index, checked)}
          checkedChildren={<EyeOutlined />}
          unCheckedChildren={<EyeInvisibleOutlined />}
        />
      ),
    },
    {
      title: t('dashboard.sectionsMgmt.sort'), width: 100,
      render: (_, record, index) => (
        <Space size="small">
          <Button
            size="small"
            icon={<ArrowUpOutlined />}
            disabled={index === 0}
            onClick={() => handleMoveSection(index, -1)}
          />
          <Button
            size="small"
            icon={<ArrowDownOutlined />}
            disabled={index === sections.length - 1}
            onClick={() => handleMoveSection(index, 1)}
          />
        </Space>
      ),
    },
  ];

  const isPresetColor = COLOR_PRESET_DATA.some(p => p.color === selectedColor);

  return (
    <div>
      {/* Header Card */}
      <Card style={{ marginBottom: 24 }} bodyStyle={{ padding: 0 }}>
        <div
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '32px 32px 28px',
            borderRadius: '8px 8px 0 0',
          }}
        >
          <Row justify="space-between" align="middle">
            <Col>
              <Space align="center" size={16}>
                <GlobalOutlined style={{ fontSize: 32, color: '#fff' }} />
                <div>
                  <Title level={3} style={{ margin: 0, color: '#fff' }}>{site.title}</Title>
                  <Space style={{ marginTop: 8 }}>
                    <Text
                      style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13 }}
                      copyable={false}
                    >
                      {getPublicUrl()}
                    </Text>
                    <Button
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={copyPublicUrl}
                      style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)' }}
                      ghost
                    >
                      {t('dashboard.copy')}
                    </Button>
                  </Space>
                </div>
              </Space>
            </Col>
            <Col>
              <Space size={12}>
                <Switch
                  checked={site.is_published}
                  onChange={handleTogglePublish}
                  checkedChildren={t('dashboard.publishedLabel')}
                  unCheckedChildren={t('dashboard.draftLabel')}
                />
                <Button
                  icon={<EyeOutlined />}
                  ghost
                  style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)' }}
                  onClick={() => window.open(getPublicUrl(), '_blank')}
                >
                  {t('dashboard.preview')}
                </Button>
              </Space>
            </Col>
          </Row>
        </div>
      </Card>

      {/* Template & Color Selection */}
      <Card
        title={
          <Space>
            <LayoutOutlined />
            <span>{t('dashboard.style.title')}</span>
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={savingStyle}
            onClick={handleSaveStyle}
          >
            {t('dashboard.style.apply')}
          </Button>
        }
        style={{ marginBottom: 24 }}
      >
        {/* Template Selection */}
        <div style={{ marginBottom: 24 }}>
          <Text strong style={{ display: 'block', marginBottom: 12, fontSize: 15 }}>
            <LayoutOutlined style={{ marginRight: 6 }} />
            {t('dashboard.style.selectTemplate')}
          </Text>
          <Row gutter={16}>
            {TEMPLATE_DATA.map(tmpl => (
              <Col key={tmpl.key} xs={24} sm={8}>
                <TemplatePreviewCard
                  template={tmpl}
                  brandColor={selectedColor}
                  selected={selectedTemplate === tmpl.key}
                  onClick={() => setSelectedTemplate(tmpl.key)}
                  t={t}
                />
              </Col>
            ))}
          </Row>
        </div>

        <Divider />

        {/* Color Scheme Selection */}
        <div>
          <Text strong style={{ display: 'block', marginBottom: 12, fontSize: 15 }}>
            <BgColorsOutlined style={{ marginRight: 6 }} />
            {t('dashboard.style.selectColor')}
          </Text>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
            {COLOR_PRESET_DATA.map(p => {
              const isSelected = selectedColor === p.color;
              const isLight = _lum(p.color) > 0.45;
              return (
                <Tooltip key={p.key} title={t(`dashboard.colors.${p.key}`)}>
                  <div
                    onClick={() => handleColorSelect(p.color)}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 4,
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{
                      width: 44,
                      height: 44,
                      borderRadius: 10,
                      background: p.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: isSelected ? `3px solid ${isLight ? _dk(p.color, 0.4) : '#333'}` : '2px solid rgba(0,0,0,0.08)',
                      transition: 'all 0.2s',
                      boxShadow: isSelected ? `0 2px 12px rgba(0,0,0,0.2)` : '0 1px 3px rgba(0,0,0,0.08)',
                    }}>
                      {isSelected && (
                        <CheckCircleFilled style={{ color: isLight ? _dk(p.color, 0.5) : '#fff', fontSize: 20, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))' }} />
                      )}
                    </div>
                    <Text style={{ fontSize: 10, color: isSelected ? '#333' : '#999' }}>{t(`dashboard.colors.${p.key}`)}</Text>
                  </div>
                </Tooltip>
              );
            })}
          </div>
          <Space>
            <Text type="secondary">{t('dashboard.style.customColor')}</Text>
            <Input
              value={customColor || (isPresetColor ? '' : selectedColor)}
              onChange={handleCustomColorChange}
              placeholder="#FF6B6B"
              style={{ width: 120 }}
              prefix={
                <div style={{
                  width: 16, height: 16, borderRadius: 4,
                  background: selectedColor, border: '1px solid #d9d9d9',
                }} />
              }
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('dashboard.style.currentColor')}{selectedColor}
            </Text>
          </Space>
        </div>

        <Divider />

        {/* Live Preview */}
        <div>
          <Text strong style={{ display: 'block', marginBottom: 12, fontSize: 15 }}>
            {t('dashboard.style.livePreview')}
          </Text>
          <LivePreview template={selectedTemplate} color={selectedColor} siteName={site.title} t={t} />
        </div>
      </Card>

      <Row gutter={24}>
        <Col span={16}>
          {/* Sections Management */}
          <Card
            title={
              <Space>
                <SettingOutlined />
                <span>{t('dashboard.sectionsMgmt.title')}</span>
              </Space>
            }
            extra={
              <Button
                type="primary"
                icon={<SaveOutlined />}
                loading={savingSections}
                onClick={handleSaveSections}
              >
                {t('dashboard.sectionsMgmt.saveOrder')}
              </Button>
            }
            style={{ marginBottom: 24 }}
          >
            {sections.length === 0 ? (
              <Text type="secondary">{t('dashboard.sectionsMgmt.noSections')}</Text>
            ) : (
              <Table
                columns={sectionColumns}
                dataSource={sections}
                rowKey={(record, index) => record.id || index}
                pagination={false}
                size="middle"
              />
            )}
          </Card>

          {/* Products Management */}
          <Card
            title={
              <Space>
                <ShoppingCartOutlined />
                <span>{t('dashboard.productsMgmt.title')}</span>
                <Tag>{siteProducts.length} {t('dashboard.productsMgmt.items')}</Tag>
              </Space>
            }
            extra={
              <Space>
                <Button icon={<LinkOutlined />} onClick={openLinkProduct}>
                  {t('dashboard.productsMgmt.addExisting')}
                </Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={openCreateProduct}>
                  {t('dashboard.productsMgmt.addNew')}
                </Button>
              </Space>
            }
            style={{ marginBottom: 24 }}
          >
            {siteProducts.length === 0 ? (
              <Empty
                description={t('dashboard.productsMgmt.noProducts')}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              >
                <Space>
                  <Button icon={<LinkOutlined />} onClick={openLinkProduct}>{t('dashboard.productsMgmt.selectFromCatalog')}</Button>
                  <Button type="primary" icon={<PlusOutlined />} onClick={openCreateProduct}>{t('dashboard.productsMgmt.createNew')}</Button>
                </Space>
              </Empty>
            ) : (
              <Table
                columns={[
                  {
                    title: t('dashboard.productsMgmt.product'), width: 260,
                    render: (_, sp) => {
                      const p = sp.product || {};
                      return (
                        <Space>
                          {p.thumbnail && (
                            <img src={p.thumbnail} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} />
                          )}
                          <div>
                            <div style={{ fontWeight: 500 }}>{p.name}</div>
                            {p.organization && <Text type="secondary" style={{ fontSize: 12 }}>{p.organization}</Text>}
                          </div>
                        </Space>
                      );
                    },
                  },
                  {
                    title: t('dashboard.productsMgmt.price'), width: 120, align: 'right',
                    render: (_, sp) => {
                      const p = sp.product || {};
                      if (sp.custom_price_note) return <Text>{sp.custom_price_note}</Text>;
                      return p.price ? `${p.currency || 'TWD'} ${p.price.toLocaleString(i18n.language)}` : '-';
                    },
                  },
                  {
                    title: t('dashboard.productsMgmt.featured'), width: 60, align: 'center',
                    render: (_, sp) => (
                      <Button
                        type="text" size="small"
                        icon={sp.is_featured ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined />}
                        onClick={() => handleToggleFeatured(sp)}
                      />
                    ),
                  },
                  {
                    title: t('dashboard.productsMgmt.actions'), width: 100,
                    render: (_, sp) => (
                      <Space size="small">
                        <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEditProduct(sp)} />
                        <Popconfirm title={t('dashboard.productsMgmt.confirmRemove')} onConfirm={() => handleRemoveProduct(sp.id)} okText={t('dashboard.productsMgmt.remove')} cancelText={t('common.cancel')}>
                          <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                      </Space>
                    ),
                  },
                ]}
                dataSource={siteProducts}
                rowKey="id"
                pagination={false}
                size="middle"
                loading={productsLoading}
              />
            )}
          </Card>

          {/* Events Management */}
          <Card
            title={
              <Space>
                <CalendarOutlined />
                <span>{t('dashboard.eventsMgmt.title')}</span>
                <Tag>{events.length} {t('dashboard.eventsMgmt.count')}</Tag>
              </Space>
            }
            extra={
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreateEvent}>
                {t('dashboard.eventsMgmt.addNew')}
              </Button>
            }
            style={{ marginBottom: 24 }}
          >
            {events.length === 0 ? (
              <Empty
                description={t('dashboard.eventsMgmt.noEvents')}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              >
                <Button type="primary" icon={<PlusOutlined />} onClick={openCreateEvent}>{t('dashboard.eventsMgmt.createFirst')}</Button>
              </Empty>
            ) : (
              <Table
                columns={[
                  {
                    title: t('dashboard.eventsMgmt.eventName'), dataIndex: 'title', width: 200,
                    render: (v, evt) => (
                      <div>
                        <div style={{ fontWeight: 500 }}>{v}</div>
                        {evt.location && <Text type="secondary" style={{ fontSize: 12 }}><EnvironmentOutlined /> {evt.location}</Text>}
                      </div>
                    ),
                  },
                  {
                    title: t('dashboard.eventsMgmt.eventDate'), dataIndex: 'event_date', width: 140,
                    render: v => v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-',
                  },
                  {
                    title: t('dashboard.eventsMgmt.quota'), width: 100, align: 'center',
                    render: (_, evt) => (
                      <span>
                        <Badge count={evt.registration_count || 0} style={{ backgroundColor: '#52c41a' }} overflowCount={999} />
                        {evt.max_attendees ? ` / ${evt.max_attendees}` : ''}
                      </span>
                    ),
                  },
                  {
                    title: t('dashboard.eventsMgmt.registration'), width: 60, align: 'center',
                    render: (_, evt) => (
                      <Button type="link" size="small" onClick={() => openRegistrations(evt)}>
                        <TeamOutlined /> {evt.registration_count || 0}
                      </Button>
                    ),
                  },
                  {
                    title: t('dashboard.eventsMgmt.active'), width: 70, align: 'center',
                    render: (_, evt) => (
                      <Switch size="small" checked={evt.is_active} onChange={() => handleToggleEventActive(evt)} />
                    ),
                  },
                  {
                    title: t('dashboard.eventsMgmt.actions'), width: 100,
                    render: (_, evt) => (
                      <Space size="small">
                        <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEditEvent(evt)} />
                        <Popconfirm title={t('dashboard.eventsMgmt.confirmDelete')} onConfirm={() => handleDeleteEvent(evt.id)} okText={t('common.delete')} cancelText={t('common.cancel')}>
                          <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                      </Space>
                    ),
                  },
                ]}
                dataSource={events}
                rowKey="id"
                pagination={false}
                size="middle"
                loading={eventsLoading}
              />
            )}
          </Card>

          {/* Blog Management */}
          <Card
            title={
              <Space>
                <ReadOutlined />
                <span>{t('dashboard.blogMgmt.title')}</span>
                <Tag>{blogPosts.length} {t('dashboard.blogMgmt.count')}</Tag>
              </Space>
            }
            extra={
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreatePost}>
                {t('dashboard.blogMgmt.addNew')}
              </Button>
            }
            style={{ marginBottom: 24 }}
          >
            {blogPosts.length === 0 ? (
              <Empty
                description={t('dashboard.blogMgmt.noPosts')}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              >
                <Button type="primary" icon={<PlusOutlined />} onClick={openCreatePost}>{t('dashboard.blogMgmt.createFirst')}</Button>
              </Empty>
            ) : (
              <Table
                columns={[
                  {
                    title: t('dashboard.blogMgmt.postTitle'), dataIndex: 'title', width: 260,
                    render: (v, post) => (
                      <div>
                        <div style={{ fontWeight: 500 }}>{v}</div>
                        {post.excerpt && <Text type="secondary" style={{ fontSize: 12 }}>{post.excerpt.substring(0, 50)}...</Text>}
                      </div>
                    ),
                  },
                  {
                    title: t('dashboard.blogMgmt.tags'), dataIndex: 'tags', width: 150,
                    render: tags => tags?.length ? tags.map(t => <Tag key={t} style={{ fontSize: 11 }}>{t}</Tag>) : '-',
                  },
                  {
                    title: t('dashboard.blogMgmt.views'), dataIndex: 'view_count', width: 70, align: 'center',
                    render: v => v || 0,
                  },
                  {
                    title: t('dashboard.blogMgmt.publishToggle'), width: 70, align: 'center',
                    render: (_, post) => (
                      <Switch size="small" checked={post.is_published} onChange={() => handleTogglePostPublish(post)} />
                    ),
                  },
                  {
                    title: t('dashboard.blogMgmt.actions'), width: 100,
                    render: (_, post) => (
                      <Space size="small">
                        <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEditPost(post)} />
                        <Popconfirm title={t('dashboard.blogMgmt.confirmDelete')} onConfirm={() => handleDeletePost(post.id)} okText={t('common.delete')} cancelText={t('common.cancel')}>
                          <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                      </Space>
                    ),
                  },
                ]}
                dataSource={blogPosts}
                rowKey="id"
                pagination={blogPosts.length > 10 ? { pageSize: 10 } : false}
                size="middle"
                loading={blogLoading}
              />
            )}
          </Card>

          {/* Site Settings */}
          <Card
            title={
              <Space>
                <SettingOutlined />
                <span>{t('dashboard.settingsMgmt.title')}</span>
              </Space>
            }
            extra={
              <Button
                type="primary"
                icon={<SaveOutlined />}
                loading={savingSettings}
                onClick={handleSaveSettings}
              >
                {t('dashboard.settingsMgmt.save')}
              </Button>
            }
          >
            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>{t('dashboard.settingsMgmt.siteTitle')}</Text>
              <Input
                value={settingsTitle}
                onChange={(e) => setSettingsTitle(e.target.value)}
                placeholder={t('dashboard.settingsMgmt.siteTitlePlaceholder')}
              />
            </div>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>{t('dashboard.settingsMgmt.siteDesc')}</Text>
              <TextArea
                value={settingsDescription}
                onChange={(e) => setSettingsDescription(e.target.value)}
                placeholder={t('dashboard.settingsMgmt.siteDescPlaceholder')}
                rows={4}
              />
            </div>
          </Card>
        </Col>

        <Col span={8}>
          {/* Owner Info */}
          <Card
            title={t('dashboard.ownerInfo.title')}
            style={{ marginBottom: 24 }}
          >
            <Text strong>{t('dashboard.ownerInfo.account')}</Text>
            <Text>{site.owner_username || '-'}</Text>
          </Card>

          {/* Google Drive */}
          <Card
            title={<Space><CloudOutlined /> {t('dashboard.gdrive.title')}</Space>}
            style={{ marginBottom: 24 }}
            extra={
              gdriveLinked.folder_id ? (
                <Space>
                  <Button size="small" icon={<LinkOutlined />} onClick={openGdrivePicker}>{t('dashboard.gdrive.change')}</Button>
                  <Popconfirm title={t('dashboard.gdrive.unlinkConfirm')} onConfirm={unlinkGdrive}>
                    <Button size="small" danger icon={<DisconnectOutlined />}>{t('dashboard.gdrive.unlink')}</Button>
                  </Popconfirm>
                </Space>
              ) : (
                <Button type="primary" size="small" icon={<FolderOutlined />} onClick={openGdrivePicker}>
                  {t('dashboard.gdrive.selectFolder')}
                </Button>
              )
            }
          >
            {gdriveLinked.folder_id ? (
              <div>
                <Space style={{ marginBottom: 12 }}>
                  <FolderOutlined style={{ color: '#faad14', fontSize: 16 }} />
                  <Text strong style={{ fontSize: 13 }}>{gdriveLinked.folder_name || gdriveLinked.folder_id}</Text>
                </Space>
                <Table
                  dataSource={gdriveFiles}
                  rowKey="id"
                  size="small"
                  loading={gdriveFilesLoading}
                  pagination={{ pageSize: 8 }}
                  columns={[
                    {
                      title: t('dashboard.gdrive.fileName'), dataIndex: 'name', ellipsis: true,
                      render: (name, record) => (
                        <Space>
                          {record.is_dir ? <FolderOutlined style={{ color: '#faad14' }} /> : <FileOutlined style={{ color: '#1890ff' }} />}
                          <Text style={{ fontSize: 12 }}>{name}</Text>
                        </Space>
                      ),
                    },
                    { title: t('dashboard.gdrive.fileSize'), dataIndex: 'size_human', width: 70, render: v => <Text style={{ fontSize: 11 }}>{v || '-'}</Text> },
                  ]}
                />
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: 24, color: '#999' }}>
                <FolderOutlined style={{ fontSize: 32, marginBottom: 8 }} /><br />
                {t('dashboard.gdrive.notLinked')}<br />
                <Text type="secondary" style={{ fontSize: 12 }}>{t('dashboard.gdrive.notLinkedDesc')}</Text>
              </div>
            )}
          </Card>

          {/* Quick Info */}
          <Card title={t('dashboard.siteInfoCard.title')}>
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary">Slug</Text>
              <br />
              <Text code>{siteConfig?.slug || site.slug}</Text>
            </div>
            <Divider style={{ margin: '12px 0' }} />
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary">{t('dashboard.siteInfoCard.status')}</Text>
              <br />
              {site.is_published
                ? <Tag color="green">{t('dashboard.publishedLabel')}</Tag>
                : <Tag>{t('dashboard.draftLabel')}</Tag>
              }
            </div>
            <Divider style={{ margin: '12px 0' }} />
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary">{t('dashboard.siteInfoCard.template')}</Text>
              <br />
              <Tag color="blue">{t(`dashboard.templates.${selectedTemplate}`) || selectedTemplate}</Tag>
            </div>
            <Divider style={{ margin: '12px 0' }} />
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary">{t('dashboard.siteInfoCard.brandColor')}</Text>
              <br />
              <Space>
                <div style={{ width: 20, height: 20, borderRadius: 4, background: selectedColor, border: '1px solid #d9d9d9', display: 'inline-block', verticalAlign: 'middle' }} />
                <Text code>{selectedColor}</Text>
              </Space>
            </div>
            <Divider style={{ margin: '12px 0' }} />
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary">{t('dashboard.siteInfoCard.sectionCount')}</Text>
              <br />
              <Text>{sections.length} {t('dashboard.siteInfoCard.sectionUnit')}</Text>
            </div>
            <Divider style={{ margin: '12px 0' }} />
            <div>
              <Text type="secondary">{t('dashboard.siteInfoCard.createdAt')}</Text>
              <br />
              <Text>
                {site.created_at
                  ? new Date(site.created_at).toLocaleString(i18n.language)
                  : '-'
                }
              </Text>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Product Modal */}
      <Modal
        title={productModalMode === 'link' ? t('dashboard.productsMgmt.linkTitle') : productModalMode === 'edit' ? t('dashboard.productsMgmt.editTitle') : t('dashboard.productsMgmt.createTitle')}
        open={productModalOpen}
        onOk={handleSaveProduct}
        onCancel={() => { setProductModalOpen(false); productForm.resetFields(); }}
        okText={productModalMode === 'link' ? t('dashboard.productsMgmt.add') : t('common.save')}
        cancelText={t('common.cancel')}
        width={640}
        confirmLoading={productSaving}
        styles={{ body: { maxHeight: '60vh', overflowY: 'auto' } }}
      >
        <Form form={productForm} layout="vertical">
          {productModalMode === 'link' ? (
            <>
              <Form.Item
                name="product_id"
                label={t('dashboard.productsMgmt.selectProduct')}
                rules={[{ required: true, message: t('dashboard.productsMgmt.selectRequired') }]}
              >
                <Select
                  showSearch
                  placeholder={t('dashboard.productsMgmt.searchPlaceholder')}
                  filterOption={false}
                  onSearch={(v) => { setCatalogSearch(v); fetchCatalog(v); }}
                  notFoundContent={<Empty description={t('dashboard.productsMgmt.noProductsAvail')} image={Empty.PRESENTED_IMAGE_SIMPLE} />}
                >
                  {catalogProducts
                    .filter(p => !siteProducts.some(sp => sp.product_id === p.id))
                    .map(p => (
                      <Select.Option key={p.id} value={p.id}>
                        <Space>
                          {p.thumbnail && <img src={p.thumbnail} alt="" style={{ width: 24, height: 24, objectFit: 'cover', borderRadius: 2 }} />}
                          <span>{p.name}</span>
                          {p.organization && <Tag style={{ fontSize: 10 }}>{p.organization}</Tag>}
                        </Space>
                      </Select.Option>
                    ))
                  }
                </Select>
              </Form.Item>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="is_featured" label={t('dashboard.productsMgmt.featuredProduct')} valuePropName="checked">
                    <Switch checkedChildren={t('dashboard.productsMgmt.featuredOn')} unCheckedChildren={t('dashboard.productsMgmt.featuredOff')} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="custom_price_note" label={t('dashboard.productsMgmt.customPriceNote')}>
                    <Input placeholder={t('dashboard.productsMgmt.customPricePlaceholder')} />
                  </Form.Item>
                </Col>
              </Row>
            </>
          ) : (
            <>
              <Form.Item name="name" label={t('dashboard.productsMgmt.productName')} rules={[{ required: true, message: t('dashboard.productsMgmt.productNameRequired') }]}>
                <Input placeholder={t('dashboard.productsMgmt.productNamePlaceholder')} />
              </Form.Item>

              <Form.Item name="short_description" label={t('dashboard.productsMgmt.shortDesc')}>
                <Input placeholder={t('dashboard.productsMgmt.shortDescPlaceholder')} maxLength={200} />
              </Form.Item>

              <Form.Item name="description" label={t('dashboard.productsMgmt.detailDesc')}>
                <Input.TextArea rows={3} placeholder={t('dashboard.productsMgmt.detailDescPlaceholder')} />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="organization" label={t('dashboard.productsMgmt.organization')}>
                    <Input placeholder={t('dashboard.productsMgmt.organizationPlaceholder')} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="thumbnail" label={t('dashboard.productsMgmt.thumbnail')}>
                    <Input placeholder="https://..." />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="price" label={t('dashboard.productsMgmt.price')}>
                    <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="currency" label={t('dashboard.productsMgmt.currency')}>
                    <Select>
                      <Select.Option value="TWD">TWD</Select.Option>
                      <Select.Option value="USD">USD</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="custom_price_note" label={t('dashboard.productsMgmt.customPriceNote')}>
                    <Input placeholder={t('dashboard.productsMgmt.customPricePlaceholder')} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="is_featured" label={t('dashboard.productsMgmt.featuredProduct')} valuePropName="checked">
                    <Switch checkedChildren={t('dashboard.productsMgmt.featuredOn')} unCheckedChildren={t('dashboard.productsMgmt.featuredOff')} />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}
        </Form>
      </Modal>

      {/* Event Modal */}
      <Modal
        title={editingEvent ? t('dashboard.eventsMgmt.editTitle') : t('dashboard.eventsMgmt.addTitle')}
        open={eventModalOpen}
        onOk={handleSaveEvent}
        onCancel={() => { setEventModalOpen(false); eventForm.resetFields(); }}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        width={640}
        confirmLoading={eventSaving}
        styles={{ body: { maxHeight: '60vh', overflowY: 'auto' } }}
      >
        <Form form={eventForm} layout="vertical">
          <Form.Item name="title" label={t('dashboard.eventsMgmt.eventNameLabel')} rules={[{ required: true, message: t('dashboard.eventsMgmt.eventNameRequired') }]}>
            <Input placeholder={t('dashboard.eventsMgmt.eventNamePlaceholder')} />
          </Form.Item>
          <Form.Item name="description" label={t('dashboard.eventsMgmt.description')}>
            <TextArea rows={3} placeholder={t('dashboard.eventsMgmt.descriptionPlaceholder')} />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="event_date" label={t('dashboard.eventsMgmt.eventDateLabel')}>
                <DatePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} placeholder={t('dashboard.eventsMgmt.eventDatePlaceholder')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="location" label={t('dashboard.eventsMgmt.location')}>
                <Input placeholder={t('dashboard.eventsMgmt.locationPlaceholder')} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="max_attendees" label={t('dashboard.eventsMgmt.maxAttendees')}>
                <InputNumber min={1} style={{ width: '100%' }} placeholder={t('dashboard.eventsMgmt.maxAttendeesPlaceholder')} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="registration_deadline" label={t('dashboard.eventsMgmt.deadline')}>
                <DatePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} placeholder={t('dashboard.eventsMgmt.deadlinePlaceholder')} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="is_active" label={t('dashboard.eventsMgmt.activeLabel')} valuePropName="checked" initialValue={true}>
                <Switch checkedChildren={t('dashboard.eventsMgmt.activeOn')} unCheckedChildren={t('dashboard.eventsMgmt.activeOff')} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Registration Modal */}
      <Modal
        title={regModal ? `${regModal.title} — ${t('dashboard.eventsMgmt.regListTitle')} (${registrations.length})` : t('dashboard.eventsMgmt.regListTitle')}
        open={!!regModal}
        onCancel={() => { setRegModal(null); setRegistrations([]); }}
        footer={null}
        width={700}
      >
        {registrations.length === 0 ? (
          <Empty description={t('dashboard.eventsMgmt.noRegistrations')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <Table
            columns={[
              { title: t('dashboard.eventsMgmt.name'), dataIndex: 'contact_name', width: 100 },
              { title: t('dashboard.eventsMgmt.company'), dataIndex: 'company_name', width: 140 },
              { title: 'Email', dataIndex: 'email', width: 180 },
              { title: t('dashboard.eventsMgmt.phone'), dataIndex: 'phone', width: 120 },
              { title: t('dashboard.eventsMgmt.regTime'), dataIndex: 'created_at', width: 140, render: v => v ? dayjs(v).format('MM-DD HH:mm') : '-' },
            ]}
            dataSource={registrations}
            rowKey="id"
            pagination={false}
            size="small"
          />
        )}
      </Modal>

      {/* Blog Modal */}
      <Modal
        title={editingPost ? t('dashboard.blogMgmt.editTitle') : t('dashboard.blogMgmt.addTitle')}
        open={blogModalOpen}
        onOk={handleSavePost}
        onCancel={() => { setBlogModalOpen(false); blogForm.resetFields(); }}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        width={720}
        confirmLoading={blogSaving}
        styles={{ body: { maxHeight: '65vh', overflowY: 'auto' } }}
      >
        <Form form={blogForm} layout="vertical">
          <Form.Item name="title" label={t('dashboard.blogMgmt.titleLabel')} rules={[{ required: true, message: t('dashboard.blogMgmt.titleRequired') }]}>
            <Input placeholder={t('dashboard.blogMgmt.titlePlaceholder')} />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="slug" label={t('dashboard.blogMgmt.slugLabel')}>
                <Input placeholder="auto-generated-from-title" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="tags" label={t('dashboard.blogMgmt.tagsLabel')}>
                <Select mode="tags" placeholder={t('dashboard.blogMgmt.tagsPlaceholder')} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="excerpt" label={t('dashboard.blogMgmt.excerptLabel')}>
            <Input placeholder={t('dashboard.blogMgmt.excerptPlaceholder')} maxLength={300} />
          </Form.Item>
          <Form.Item name="content" label={t('dashboard.blogMgmt.contentLabel')} rules={[{ required: true, message: t('dashboard.blogMgmt.contentRequired') }]}>
            <TextArea rows={10} placeholder={t('dashboard.blogMgmt.contentPlaceholder')} />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="featured_image" label={t('dashboard.blogMgmt.featuredImage')}>
                <Input placeholder="https://..." />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="is_published" label={t('dashboard.blogMgmt.publishedLabel')} valuePropName="checked">
                <Switch checkedChildren={t('dashboard.blogMgmt.publishedOn')} unCheckedChildren={t('dashboard.blogMgmt.publishedOff')} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Google Drive Folder Picker Modal */}
      <Modal
        title={t('dashboard.gdrive.modalTitle')}
        open={gdriveModalOpen}
        onCancel={() => setGdriveModalOpen(false)}
        footer={null}
        width={650}
      >
        <Breadcrumb style={{ marginBottom: 12 }}>
          <Breadcrumb.Item>
            <Button type="link" size="small" onClick={() => navigateGdriveBreadcrumb(-1)}>My Drive</Button>
          </Breadcrumb.Item>
          {gdrivePath.map((p, idx) => (
            <Breadcrumb.Item key={p.id}>
              <Button type="link" size="small" onClick={() => navigateGdriveBreadcrumb(idx)}>{p.name}</Button>
            </Breadcrumb.Item>
          ))}
        </Breadcrumb>

        <Input.Search
          placeholder={t('dashboard.gdrive.searchPlaceholder')}
          allowClear
          value={gdriveSearch}
          onChange={(e) => setGdriveSearch(e.target.value)}
          style={{ marginBottom: 12 }}
        />

        {gdrivePath.length > 0 && (
          <Button
            type="primary"
            size="small"
            style={{ marginBottom: 12 }}
            onClick={() => {
              const current = gdrivePath[gdrivePath.length - 1];
              selectGdriveFolder(current.id, current.name);
            }}
          >
            {t('dashboard.gdrive.selectThisFolder')}{gdrivePath[gdrivePath.length - 1]?.name}
          </Button>
        )}

        <Table
          dataSource={gdriveSearch ? gdriveFolders.filter(f => f.name.toLowerCase().includes(gdriveSearch.toLowerCase())) : gdriveFolders}
          rowKey="id"
          size="small"
          loading={gdriveLoading}
          pagination={{ pageSize: 15 }}
          onRow={(record) => ({
            onDoubleClick: () => navigateGdriveFolder(record.id, record.name),
            style: { cursor: 'pointer' },
          })}
          columns={[
            {
              title: t('dashboard.gdrive.folderName'), dataIndex: 'name',
              render: (name) => (
                <Space>
                  <FolderOutlined style={{ color: '#faad14' }} />
                  <Text>{name}</Text>
                </Space>
              ),
            },
          ]}
        />
      </Modal>
    </div>
  );
}

function hexLuminance(hex) {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

function darken(hex, amt = 0.2) {
  const h = hex.replace('#', '');
  const f = 1 - amt;
  const r = Math.round(parseInt(h.substring(0, 2), 16) * f);
  const g = Math.round(parseInt(h.substring(2, 4), 16) * f);
  const b = Math.round(parseInt(h.substring(4, 6), 16) * f);
  return `rgb(${r},${g},${b})`;
}

function LivePreview({ template, color, siteName, t }) {
  const isModern = template === 'modern';
  const isClassic = template === 'classic';
  const isMinimal = template === 'minimal';
  const lum = hexLuminance(color);
  const bright = lum > 0.55;
  const darkColor = bright ? darken(color, 0.35) : color;

  const headerBg = isMinimal ? '#ffffff'
    : isClassic ? darkColor
    : `linear-gradient(135deg, ${darkColor}, ${darken(darkColor, 0.15)})`;

  const bodyBg = isModern ? '#f5f7fa' : isClassic ? '#ffffff' : '#fafafa';
  const cardRadius = isModern ? 12 : isClassic ? 4 : 0;
  const cardBorder = isMinimal ? '1px solid #e8e8e8' : 'none';
  const cardShadow = isModern ? '0 4px 16px rgba(0,0,0,0.08)' : isClassic ? '0 2px 8px rgba(0,0,0,0.06)' : 'none';

  const heroBg = isMinimal ? '#fff'
    : isClassic ? `linear-gradient(180deg, ${darkColor}, ${darken(darkColor, 0.1)})`
    : `linear-gradient(135deg, ${darkColor}, ${darken(darkColor, 0.2)})`;

  const btnStyle = isMinimal
    ? { border: `2px solid ${color}`, color: color, background: '#fff', borderRadius: 0, fontWeight: 600 }
    : isClassic
    ? { background: '#fff', color: darkColor, border: 'none', borderRadius: 4, fontWeight: 600 }
    : { background: '#fff', color: darkColor, border: 'none', borderRadius: 24, fontWeight: 600 };

  return (
    <div style={{
      border: '1px solid #e8e8e8',
      borderRadius: 8,
      overflow: 'hidden',
      maxWidth: '100%',
    }}>
      {/* Header */}
      <div style={{
        background: headerBg,
        padding: '10px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: isMinimal ? '1px solid #e8e8e8' : 'none',
      }}>
        <Text strong style={{ color: isMinimal ? '#333' : '#fff', fontSize: 14 }}>
          {siteName || 'My Site'}
        </Text>
        <Space size={16}>
          {[t('dashboard.preview.home'), t('dashboard.preview.products'), t('dashboard.preview.events'), t('dashboard.preview.contact')].map((n, i) => (
            <Text key={n} style={{
              color: isMinimal ? (i === 0 ? '#333' : '#666') : (i === 0 ? '#fff' : 'rgba(255,255,255,0.7)'),
              fontSize: 12,
              fontWeight: i === 0 ? 600 : 400,
              background: i === 0 && !isMinimal ? 'rgba(255,255,255,0.18)' : i === 0 && isMinimal ? `${color}10` : 'transparent',
              padding: '2px 8px',
              borderRadius: 4,
            }}>{n}</Text>
          ))}
        </Space>
      </div>

      {/* Hero */}
      <div style={{
        background: heroBg,
        padding: '28px 20px',
        textAlign: 'center',
        borderBottom: isMinimal ? `3px solid ${color}` : 'none',
      }}>
        <div style={{
          fontSize: 18,
          fontWeight: isMinimal ? 300 : 700,
          color: isMinimal ? '#1a1a2e' : '#fff',
          marginBottom: 6,
          letterSpacing: isMinimal ? 2 : 0,
        }}>
          {siteName || t('dashboard.preview.brandName')}
        </div>
        <div style={{ fontSize: 12, color: isMinimal ? '#888' : 'rgba(255,255,255,0.8)', marginBottom: 12 }}>
          {t('dashboard.preview.subtitle')}
        </div>
        <button style={{
          padding: '6px 20px',
          fontSize: 12,
          cursor: 'pointer',
          ...btnStyle,
        }}>
          {t('dashboard.preview.inquire')}
        </button>
      </div>

      {/* Content */}
      <div style={{ background: bodyBg, padding: '16px 20px' }}>
        <div style={{
          fontSize: 13, fontWeight: 600, marginBottom: 10,
          color: '#1a1a2e',
          textAlign: isMinimal ? 'left' : 'center',
          borderBottom: isMinimal ? `2px solid ${color}` : 'none',
          paddingBottom: isMinimal ? 6 : 0,
          display: 'inline-block',
        }}>
          {t('dashboard.preview.featured')}
        </div>
        <Row gutter={10}>
          {[1, 2, 3].map(i => (
            <Col key={i} span={8}>
              <div style={{
                background: '#fff',
                borderRadius: cardRadius,
                border: cardBorder,
                boxShadow: cardShadow,
                overflow: 'hidden',
                marginBottom: 8,
              }}>
                <div style={{
                  height: 50,
                  background: isModern
                    ? `linear-gradient(135deg, ${color}15, ${color}30)`
                    : isClassic
                    ? '#f5f5f5'
                    : '#fafafa',
                  borderBottom: isMinimal ? '1px solid #f0f0f0' : 'none',
                }} />
                <div style={{ padding: '6px 8px' }}>
                  <div style={{ height: 6, width: '70%', background: '#e8e8e8', borderRadius: 3, marginBottom: 4 }} />
                  <div style={{ height: 4, width: '90%', background: '#f0f0f0', borderRadius: 2 }} />
                </div>
              </div>
            </Col>
          ))}
        </Row>
      </div>

      {/* Footer */}
      <div style={{
        background: isMinimal ? '#fff' : '#001529',
        padding: '8px 20px',
        textAlign: 'center',
        borderTop: isMinimal ? '1px solid #e8e8e8' : 'none',
      }}>
        <Text style={{
          color: isMinimal ? '#999' : 'rgba(255,255,255,0.45)',
          fontSize: 10,
        }}>
          Powered by SPO Platform
        </Text>
      </div>
    </div>
  );
}
